import { BankrateOffer, MortgageRateTableDimensions } from '@bankrate/mortgage-types'
import { EventField, ProductAttributes, MortgageTrackingProduct, ProductList, ListingType } from 'types/tracking'
import { BankrateDisplayOffer } from './offers'
import { renameTerm } from './formatTermNames'
import { DisplayTab } from 'store/models/dimensions'

interface BaseImpressionParams {
  offers: BankrateOffer[]
  trackingInfo: {
    listId: string
  }
  dimensions: MortgageRateTableDimensions
}

export const makeBaseImpression = (
  { offers, trackingInfo, dimensions }: BaseImpressionParams,
  activeDisplayTab: DisplayTab
): ProductList => {
  return {
    listId: trackingInfo.listId,
    field: dimensionsToFields(dimensions),
    product: offers.map((offer, index) => getProduct(offer, index, dimensions.mortgageType, activeDisplayTab)),
  }
}

export const dimensionsToFields = (dimensions: MortgageRateTableDimensions): EventField[] => {
  const { mortgageType, zipCode, user } = dimensions
  const activeProduct = dimensions[mortgageType.toLocaleLowerCase() as keyof MortgageRateTableDimensions] as {
    points: string
    propertyType: string
    propertyUse: string
    loanTerms: string[]
  }

  const defaultFields: EventField[] = [
    {
      fieldType: 'PRODUCTTYPE',
      fieldName: 'mortgagetype',
      fieldValue: mortgageType,
      fieldLabel: 'mortgage type',
    },
    {
      fieldType: 'ZIPCODE',
      fieldName: 'zipcode',
      fieldValue: zipCode,
      fieldLabel: 'zip code',
    },
    {
      fieldType: 'CREDITSCORE',
      fieldName: 'creditscore',
      fieldValue: String(user.creditScore),
      fieldLabel: 'credit score',
    },
    {
      fieldType: 'POINTS',
      fieldName: 'points',
      fieldValue: activeProduct.points,
      fieldLabel: 'points',
    },
    {
      fieldType: 'UNKNOWN',
      fieldName: 'firsttimebuyer',
      fieldValue: String(user.firstTimeHomebuyer),
      fieldLabel: 'first time homebuyer',
    },
    {
      fieldType: 'UNITTYPE',
      fieldName: 'propertytype',
      fieldValue: activeProduct.propertyType,
      fieldLabel: 'property type',
    },
    {
      fieldType: 'UNKNOWN',
      fieldName: 'propertyuse',
      fieldValue: activeProduct.propertyUse,
      fieldLabel: 'property use',
    },
    {
      fieldType: 'UNKNOWN',
      fieldName: 'formermiltary',
      fieldValue: user.veteranStatus,
      fieldLabel: 'military/veteran',
    },
    {
      fieldType: 'UNKNOWN',
      fieldName: 'quickclosing',
      fieldValue: String(user.quickClosing),
      fieldLabel: 'quick closing',
    },
    {
      fieldType: 'UNKNOWN',
      fieldName: 'fhaloan',
      fieldValue: String(user.fha),
      fieldLabel: 'fha loan',
    },
  ]

  const productTypeFields = getProductTypeFields(dimensions)

  const termFields = getTermFields(activeProduct.loanTerms)

  return [...defaultFields, ...productTypeFields, ...termFields]
}

const getTermFields = (selectedTerms: string[]) =>
  [
    '30yr',
    '20yr',
    '15yr',
    '10yr',
    '3-1arm',
    '3-6arm',
    '5-1arm',
    '5-6arm',
    '7-1arm',
    '7-6arm',
    '10-1arm',
    '10-6arm',
  ].map(term => ({
    fieldName: term,
    fieldType: 'TERM',
    fieldValue: String(selectedTerms.includes(term)),
    fieldLabel: renameTerm(term, 'year'),
  }))

const getProductTypeFields = (dimensions: MortgageRateTableDimensions): EventField[] => {
  if (dimensions.mortgageType === 'Purchase') {
    return [
      {
        fieldType: 'UNKNOWN',
        fieldName: 'purchaseprice',
        fieldValue: String(dimensions.purchase.price),
        fieldLabel: 'purchase price',
      },
      {
        fieldType: 'DOWNPAYMENT',
        fieldName: 'downpayment',
        fieldValue: String(dimensions.purchase.downPayment),
        fieldLabel: 'down payment',
      },
      {
        fieldType: 'UNKNOWN',
        fieldName: 'downpaymentpercentage',
        fieldValue: String((dimensions.purchase.downPayment / dimensions.purchase.price) * 100),
        fieldLabel: 'down payment',
      },
    ]
  }

  return [
    {
      fieldType: 'COLLATERALVALUE',
      fieldName: 'propertyvalue',
      fieldValue: String(dimensions.refinance.propertyValue),
      fieldLabel: 'property value',
    },
    {
      fieldType: 'BALANCE',
      fieldName: 'currentloanbalance',
      fieldValue: String(dimensions.refinance.loanAmount),
      fieldLabel: 'current loan balance',
    },
    {
      fieldType: 'UNKNOWN',
      fieldName: 'cashoutamount',
      fieldValue: String(dimensions.refinance.cashOutAmount),
      fieldLabel: 'cash out amount',
    },
  ]
}

export const getProductAttributes = (offers: BankrateDisplayOffer[], loanAmount: number): ProductAttributes[] => {
  return offers.map((offer, index) => {
    let isFixedTerm
    let term
    let termUnit: '' | 'years'
    if (offer.type === 'sponsored') {
      isFixedTerm = true
      term = { termLength: 30 }
      termUnit = 'years'
    } else {
      isFixedTerm = offer.product.type !== 'adjustable'
      term = isFixedTerm ? { termLength: offer.product.fixedMonths / 12 } : {}
      termUnit = offer.product.type === 'fixed' ? 'years' : ''
    }
    let listingType = offer.type as ListingType
    if (offer.type === 'rate') {
      listingType = 'standard'
    }
    if (offer.rowType === 'extra') {
      listingType = 'additional'
    }

    const isSponsored = offer.type === 'sponsored'

    return {
      ...term,
      loanAmount,
      apr: offer.apr ?? null,
      matchType: '0%',
      rate: offer.rate ?? null,
      bankrateScore: '0',
      productId: offer.id,
      points: offer?.product?.points ?? null,
      fees: !isSponsored ? Math.round(offer.upFrontCosts || 0) : 0,
      productVertical: 'MORTGAGES',
      sectionalPosition: index + 1,
      rating: offer.advertiser.reviews?.count ? String(offer.advertiser.reviews.averageRating) : 'no rating',
      termUnit,
      listingType: offer.isFeatured ? 'featured' : listingType,
      adSetId: offer.adSetId ? Number(offer.adSetId) : 0,
      fiveYearCost: offer.fiveYearCost ? Math.round(offer.fiveYearCost) : null,
      eightYearCost: offer.eightYearCost ? Math.round(offer.eightYearCost) : null,
    }
  })
}

export const getProduct = (
  offer: BankrateDisplayOffer,
  index: number,
  mortgageType: string,
  activeDisplayTab: DisplayTab,
  text: string = ''
): MortgageTrackingProduct => {
  let location = offer.locationOverride || ('rate-table' as MortgageTrackingProduct['location'])
  if (activeDisplayTab !== 'all') location = 'featured tab' as MortgageTrackingProduct['location']
  if (offer.rowType === 'extra') location = 'additional offers' as MortgageTrackingProduct['location']

  const isSponsored = offer.type === 'sponsored'
  return {
    // @ts-ignore @TODO: remove once gateway consolidates offer contracts
    productId: isSponsored ? offer.offerId : offer.id,
    position: index + 1,
    location,
    category: mortgageType,
    // @ts-ignore @TODO: remove once gateway consolidates offer contracts
    brand: isSponsored ? offer?.creative?.advertiserDisplayName : offer.advertiser.name,
    variant: isSponsored ? '0' : String(offer.apr),
    price: isSponsored ? 0 : offer.estimatedPayment,
    text,
  }
}
